import React from 'react';
import { motion } from 'framer-motion';
import { MdLogout, MdFacebook } from 'react-icons/md';
import type { Integration } from './types';

interface IntegrationCardProps {
  integration: Integration;
  onConnect: () => void;
  onInstagramFacebookConnect?: () => void;
  onInstagramDirectConnect?: () => void;
  onDeactivate: () => void;
  onViewDetails: () => void;
  isActivating: boolean;
  isDeactivating: boolean;
  isLoadingIntegrations?: boolean;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  integration,
  onConnect,
  onInstagramFacebookConnect,
  onInstagramDirectConnect,
  onDeactivate,
  onViewDetails,
  isActivating = false,
  isDeactivating = false,
  isLoadingIntegrations = false,
}) => {
  const isConnected = integration.status === 'connected';
  const isInstagram = integration.type.toLowerCase() === 'instagram';
  const isDisabled = isActivating || isLoadingIntegrations;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="relative rounded-xl p-6 hover:border-opacity-30 transition-all h-full flex flex-col overflow-hidden"
      style={{
        backdropFilter: 'blur(10px)',
        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 14px 0px',
        background: '#2a2a2a',
        border: '1px solid rgba(224, 224, 224, 0.13)'
      }}
    >
      {/* Coming Soon Ribbon */}
      {integration.comingSoon && (
        <div className="absolute top-4 -right-10 transform rotate-45 py-1 px-12 text-xs font-bold text-white z-10"
          style={{
            background: 'linear-gradient(90deg, #EE4423, #FF7A59)',
            boxShadow: '0 2px 8px rgba(238, 68, 37, 0.4)'
          }}
        >
          COMING SOON
        </div>
      )}

      {/* Deactivate Icon - Top Right */}
      {isConnected && !integration.comingSoon && onDeactivate && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDeactivate();
          }}
          disabled={isDeactivating}
          className="absolute top-4 right-4 cursor-pointer w-8 h-8 rounded-full bg-brand-primary hover:bg-[#ee4423] flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          title="Logout"
        >
          <MdLogout size={16} className="text-white" />
        </button>
      )}

      {/* Icon */}
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
        style={{ backgroundColor: `${integration.color}15` }}
      >
        <div style={{ color: integration.color }} className="text-3xl">
          {integration.icon}
        </div>
      </div>

      {/* Name */}
      <h3 className="text-lg font-semibold text-white mb-3">
        {integration.name}
      </h3>

      {/* Description */}
      <p className="text-gray-400 text-xs leading-relaxed mb-6 flex-grow">
        {integration.description}
      </p>

      {/* Action Links */}
      {!integration.comingSoon && (
        <div className="mt-auto">
          {isConnected ? (
            /* View Details Link for Connected */
            <button
              onClick={onViewDetails}
              className="text-brand-primary font-normal text-sm capitalize hover:underline cursor-pointer transition-all"
            >
              View Details
            </button>
          ) : isInstagram ? (
            <div className="flex items-center gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onInstagramFacebookConnect?.();
                }}
                disabled={isDisabled}
                className="text-brand-primary font-normal text-sm capitalize hover:underline disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-all"
              >
                {isLoadingIntegrations ? 'Loading...' : isActivating ? 'Connecting...' : 'With Facebook'}
              </button>
              <span className="text-gray-500">|</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onInstagramDirectConnect?.();
                }}
                disabled={isDisabled}
                className="text-brand-primary font-normal text-sm capitalize hover:underline disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-all"
              >
                {isLoadingIntegrations ? 'Loading...' : isActivating ? 'Connecting...' : 'Instagram'}
              </button>
            </div>
          ) : (
            /* Other Platforms: Text-based link */
            <button
              onClick={(e) => {
                e.stopPropagation();
                onConnect?.();
              }}
              disabled={isDisabled}
              className="text-brand-primary font-normal text-sm capitalize hover:underline disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-all"
            >
              {isLoadingIntegrations ? 'Loading...' : isActivating ? 'Connecting...' : integration.type.toLowerCase() === 'email' ? 'Activate' : 'Connect Now'}
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default IntegrationCard;
